// src/components/HomePage/ArticlesSection.jsx
import React, { useState } from "react";
import Fade from "react-reveal/Fade";

import nigelleImg from "../../assets/images/appImages/nizelle.jpg";
import hijamaImg from "../../assets/images/appImages/hija.jpg";
import plantesImg from "../../assets/images/appImages/phyto1.jpg";
import sommeilImg from "../../assets/images/appImages/Sommeil.jpg";
import curcumaImg from "../../assets/images/appImages/Curcuma.jpg";
import roqyaImg from "../../assets/images/appImages/roqya.jpg";
import { WHATSAPP_RDV_URL } from "../../utils/whatsapp";

const categories = ["Toutes", "Phytothérapie", "Médecine prophétique", "Hijama", "Roqya"];

// tier: utilisé UNIQUEMENT pour l'affichage de l'onglet "Toutes"
// ("featured" = grand format, "row" = ligne horizontale, "grid" = grille 3 colonnes)
const conseils = [
  {
    id: 1,
    categorie: "Médecine prophétique",
    date: "12 août 2025",
    duree: "6 min de lecture",
    titre: "La Nigelle (Habba Sawda) : ce que dit la tradition, ce que confirme la recherche",
    description:
      "Surnommée le « remède à tout sauf à la mort » dans les hadiths, la nigelle fait l'objet d'études sérieuses. Tour d'horizon équilibré.",
    image: nigelleImg,
    badge: "Populaire",
    tier: "featured",
  },
  {
    id: 2,
    categorie: "Hijama",
    duree: "5 min de lecture",
    titre: "Comment se déroule une séance de Hijama chez Fouta Bio ?",
    image: hijamaImg,
    tier: "row",
  },
  {
    id: 3,
    categorie: "Phytothérapie",
    date: "28 juillet 2025",
    duree: "7 min de lecture",
    titre: "Naturel ne veut pas dire sans risque : précautions avec les plantes",
    description:
      "Interactions médicamenteuses, contre-indications, dosage : ce qu'il faut savoir avant de consommer une plante médicinale.",
    image: plantesImg,
    badge: "Important",
    tier: "row",
  },
  {
    id: 4,
    categorie: "Médecine prophétique",
    duree: "4 min",
    titre: "Sommeil et bien-être : les recommandations de la Sunnah revisitées",
    description:
      "Le coucher tôt, l'hygiène du sommeil, les invocations du soir — des pratiques aux fondements solides.",
    image: sommeilImg,
    tier: "grid",
  },
  {
    id: 5,
    categorie: "Phytothérapie",
    duree: "5 min",
    titre: "Curcuma et inflammation : données actuelles et conseils d'utilisation",
    description:
      "La curcumine est prometteuse, mais sa biodisponibilité dépend d'une association clé. Ce qu'il faut comprendre.",
    image: curcumaImg,
    tier: "grid",
  },
  {
    id: 6,
    categorie: "Roqya",
    date: "5 juillet 2025",
    duree: "8 min",
    titre: "Comprendre la Roqya : ce qu'elle est, ce qu'elle n'est pas",
    description:
      "La Roqya coranique est un acte spirituel encadré. Distinguer les pratiques authentiques des dérives.",
    image: roqyaImg,
    tier: "grid",
  },
];

const ConseilsSection = () => {
  const [activeCategory, setActiveCategory] = useState("Toutes");
  const isToutes = activeCategory === "Toutes";

  let featured, rowConseils, gridConseils;

  if (isToutes) {
    // Structure fixe : 1 featured + row + grid, telle que définie par "tier"
    featured = conseils.find((c) => c.tier === "featured");
    rowConseils = conseils.filter((c) => c.tier === "row");
    gridConseils = conseils.filter((c) => c.tier === "grid");
  } else {
    // Structure dynamique par catégorie : le 1er article devient la vedette,
    // les suivants s'affichent en ligne. Pas de grille ici.
    const items = conseils.filter((c) => c.categorie === activeCategory);
    featured = items[0];
    rowConseils = items.slice(1);
    gridConseils = [];
  }

  return (
    <>
      {/* Header */}
      <section className="articles-header-section">
        <div className="container-articles">
          <Fade bottom>
            <p className="articles-eyebrow">Ressources &amp; éducation</p>
            <h2 className="articles-title">Conseils et ressources</h2>
            <p className="articles-subtitle">
              Des contenus accessibles pour mieux comprendre la phytothérapie,
              la médecine prophétique, la Hijama, les précautions et les
              habitudes de vie.
            </p>
          </Fade>
        </div>
      </section>

      {/* Body */}
      <section className="articles-body-section">
        <div className="container-articles">
          {/* Filtres */}
          <div className="articles-filters">
            {categories.map((cat) => (
              <button
                key={cat}
                className={`articles-filter-btn ${
                  activeCategory === cat ? "active" : ""
                }`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Article vedette (grand format) */}
          {featured && (
            <Fade bottom>
              <a href="#" className="articles-featured-card">
                <div className="articles-featured-image-wrap">
                  {featured.badge && (
                    <span className="articles-badge-populaire">
                      {featured.badge}
                    </span>
                  )}
                  <img
                    src={featured.image}
                    alt={featured.titre}
                    className="articles-featured-image"
                  />
                </div>
                <div className="articles-featured-body">
                  <p className="articles-meta">
                    <span className="articles-categorie">
                      {featured.categorie}
                    </span>
                    {featured.date && (
                      <>
                        {" "}
                        <span className="articles-dot">·</span> {featured.date}
                      </>
                    )}{" "}
                    <span className="articles-dot">·</span> {featured.duree}
                  </p>
                  <h3 className="articles-featured-title">{featured.titre}</h3>
                  {featured.description && (
                    <p className="articles-featured-desc">
                      {featured.description}
                    </p>
                  )}
                  <span className="articles-link">Lire l'article →</span>
                </div>
              </a>
            </Fade>
          )}

          {/* Liste secondaire (lignes horizontales, sans description) */}
          {rowConseils.length > 0 && (
            <div className="articles-row-list">
              {rowConseils.map((item) => (
                <a href="#" key={item.id} className="articles-row-card">
                  <img
                    src={item.image}
                    alt={item.titre}
                    className="articles-row-image"
                  />
                  <div className="articles-row-body">
                    <span className="articles-categorie">
                      {item.categorie}
                    </span>
                    <p className="articles-row-title">{item.titre}</p>
                    <p className="articles-row-duree">{item.duree}</p>
                  </div>
                </a>
              ))}
            </div>
          )}

          {/* Grille 3 colonnes (uniquement onglet "Toutes") */}
          {gridConseils.length > 0 && (
            <div className="articles-grid">
              {gridConseils.map((item) => (
                <Fade bottom key={item.id}>
                  <a href="#" className="articles-grid-card">
                    <img
                      src={item.image}
                      alt={item.titre}
                      className="articles-grid-image"
                    />
                    <div className="articles-grid-body">
                      <p className="articles-meta">
                        <span className="articles-categorie">
                          {item.categorie}
                        </span>{" "}
                        <span className="articles-dot">·</span> {item.duree}
                      </p>
                      <h4 className="articles-grid-title">{item.titre}</h4>
                      {item.description && (
                        <p className="articles-grid-desc">
                          {item.description}
                        </p>
                      )}
                      <span className="articles-link">Lire →</span>
                    </div>
                  </a>
                </Fade>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="articles-cta-section">
        <div className="container-articles text-center">
          <p className="articles-cta-eyebrow">Une question ?</p>
          <h3 className="articles-cta-title">
            Vous cherchez un conseil personnalisé ?
          </h3>
          <p className="articles-cta-text">
            Ces ressources sont générales. Pour une situation spécifique,
            contactez-nous ou prenez rendez-vous.
          </p>
          <div className="articles-cta-buttons">
            
             <a href={WHATSAPP_RDV_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="articles-btn-whatsapp"
            >
              Écrire sur WhatsApp
            </a>
            <a
              href={WHATSAPP_RDV_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="articles-btn-rdv"
            >
              Prendre rendez-vous
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default ConseilsSection;